import { useCallback, useState } from 'react'
import isString from 'lodash/isString'
import { parseQuery } from '@chilipiper/utils'
import { setTrackingUserId } from '@chilipiper/service/src/tracking'
import { bookMe, reports } from '@chilipiper/service'
import { setSessionId } from '@chilipiper/service/src/session'
import { captureException, captureMessage, SeverityLevels, withScope } from '@chilipiper/sentry'
import { SingleUseLinkData } from '@chilipiper/api-type-def/src/bookingLinks'
import { get } from '@chilipiper/service/src/api'
import { BookMeSession, QueueAlgorithm, Report, Template } from '@chilipiper/api-type-def'
import { injectCss } from '@chilipiper/meetings-components'
import { logRocketTrack } from '@chilipiper/tracking'
import { useAvailabilityContext } from './AvailabilityContext'
import { LoadQueueData } from './loader/types'

export interface NavigatedError {
  errorCode: number
  errorMessage: string
}

export const useGuestSession = (
  tenantDomain: string,
  view: string,
  assignee?: string,
  search?: string,
  queue?: string,
  singleUseLinkData?: SingleUseLinkData,
  secret?: string
) => {
  const { setAvailabilityState } = useAvailabilityContext()
  const [newSessionCreated, setNewSessionCreated] = useState(false)
  const [templateId, setTemplateId] = useState('')
  const [formId, setFormId] = useState<string | undefined>(undefined)
  const [meetingTypeSlug, setMeetingTypeSlug] = useState<string | undefined>(undefined)
  const [isPersonalWorkspace, setIsPersonalWorkspace] = useState(true)
  const [queueAlgorithm, setQueueAlgorithm] = useState<QueueAlgorithm>('RoundRobin')
  const [navigatedError, setNavigatedError] = useState<NavigatedError | undefined>()
  const [cancelled, setCancelled] = useState(false)
  const [loading, setLoading] = useState(true)
  const checkMeetingStatus = useCallback(async () => {
    const { rescheduleId } = parseQuery()

    if (!rescheduleId || !isString(rescheduleId)) {
      return Promise.resolve({})
    }

    try {
      const report = await reports.byId(rescheduleId, tenantDomain, secret)
      return report
    } catch (error) {
      captureException(error, scope => {
        scope.setLevel(SeverityLevels.Warning)
        scope.setContext('Report Error', {
          Domain: tenantDomain,
          'Reschedule Id': rescheduleId,
        })
        return scope
      })
    }
  }, [secret, tenantDomain])

  const getQueueParam = useCallback(
    (queue?: string, linkName?: string) => {
      if (linkName) {
        return `/${linkName}`
      }
      const isSingleUseLink = queue === 'once'

      if (isSingleUseLink) {
        return singleUseLinkData?.type === 'Personal' ? '/me' : `/${singleUseLinkData?.queue}`
      }
      return queue ? `/${queue}` : ''
    },
    [singleUseLinkData]
  )

  const createSessionLink = useCallback(
    (linkName?: string) => {
      const queueParam = getQueueParam(queue, linkName)

      let sessionLink = `book-me/session/${tenantDomain}${queueParam}`
      sessionLink = assignee ? `${sessionLink}/${assignee}` : sessionLink
      sessionLink = `${sessionLink}${search}`
      return sessionLink
    },
    [getQueueParam, queue, tenantDomain, assignee, search, singleUseLinkData]
  )
  const getSession = useCallback(
    async (sessionLink: string) => {
      const result = await get(sessionLink).json<BookMeSession>()
      if (!result.success) {
        return result
      }

      if (result.sessionId) {
        setSessionId(result.sessionId)
        setTrackingUserId(result.sessionId)
      } else {
        withScope(scope => {
          scope.setContext('Tenant', {
            Domain: tenantDomain,
            'Session Link': sessionLink,
          })
          captureMessage('Session Id is empty')
        })

        logRocketTrack('Session ID is empty', {
          domain: tenantDomain,
          sessionLink,
        })
      }
      return result
    },
    [tenantDomain]
  )

  const updateMeetingTypeAndQueue = async (
    meetingTypeId?: string,
    userId?: string,
    queueId?: string
  ) => {
    if (!meetingTypeId) {
      setIsPersonalWorkspace(true)
      return
    }
    if (userId) {
      setIsPersonalWorkspace(true)
      return
    }
    setLoading(true)
    await Promise.all([
      get(`book-me/template/${meetingTypeId}`).json<Template>(),
      queueId
        ? get(`book-me/queue/${queueId}`).json<LoadQueueData | null>()
        : Promise.resolve(null),
    ]).then(([templateResponse, queueResponse]) => {
      setMeetingTypeSlug(templateResponse.linkName)
      setFormId(templateResponse.form)
      setIsPersonalWorkspace(Boolean(templateResponse.userId))
      if (queueResponse) {
        setQueueAlgorithm(queueResponse.algorithm)
      }
      setLoading(false)
    })
  }

  const createNewSession = useCallback(
    async (linkName?: string) => {
      setLoading(true)
      const report = (await checkMeetingStatus()) as Report | { status?: string }

      if (report?.status === 'cancelled') {
        setCancelled(true)
        setLoading(false)

        return
      }

      const sessionLink = createSessionLink(linkName)
      if (view === 'book' || view === 'booked') {
        await getSession(sessionLink).then(async result => {
          if (!result.success) {
            setNavigatedError({
              errorCode: result.code,
              errorMessage: result.reason,
            })
            setLoading(false)
          } else {
            setNavigatedError(undefined)
            setTemplateId(result.bookMe?.bookMeeting?.template || '')
            await updateMeetingTypeAndQueue(
              result.bookMe.bookMeeting.template,
              result.bookMe.bookMeeting.userId,
              result.bookMe.bookMeeting.queueId
            )
            setCancelled(result.bookMe?.isCanceled)
            setLoading(false)
          }
        })
        injectCss(() => setLoading(false))
      } else {
        setNewSessionCreated(true)
        return getSession(sessionLink)
          .then(async result => {
            if (!result.success) {
              setNavigatedError({
                errorCode: result.code,
                errorMessage: result.reason,
              })
              setLoading(false)
            } else {
              setNavigatedError(undefined)
              setTemplateId(result.bookMe?.bookMeeting?.template || '')
              await updateMeetingTypeAndQueue(
                result.bookMe.bookMeeting.template,
                result.bookMe.bookMeeting.userId,
                result.bookMe.bookMeeting.queueId
              )
              setCancelled(result.bookMe?.isCanceled)
              if (!linkName) {
                injectCss(() => setLoading(false))
              } else {
                setLoading(false)
              }
            }
          })
          .catch(response => {
            setLoading(false)
            setNewSessionCreated(false)
            bookMe.removeGuestSession()
            setAvailabilityState({
              response: response,
              endpoint: sessionLink,
            })
          })
      }
    },
    [checkMeetingStatus, getSession, view, createSessionLink, setAvailabilityState]
  )
  return {
    newSessionCreated,
    createNewSession,
    templateId,
    meetingTypeSlug,
    isPersonalWorkspace,
    queueAlgorithm,
    cancelled,
    loading,
    setLoading,
    formId,
    navigatedError,
  }
}
